import { useState } from "react";
import MsTable from "../../../../components/Widgets/Table";
import GiftCategoryModal from "./GiftCategoryModal";
import { FaTrash } from "react-icons/fa";
import { colType } from "../../../../Types";
import FloatAddButton from "../../../../components/Widgets/FloatAddButton";
import useCategoriesQuery from "../hooks/useCategoriesQuery";
import useAddCategoryMutation from "../hooks/useAddCategoryMutation";
import useRemoveCategoryMutation from "../hooks/useRemoveCategoryMutation";
import { GiftCategory } from "../../../../api/giftCategories";
import uploadFile from "../../../../utils/uploadFiles";
import useUpdateCategoryMutation from "../hooks/useUpdateCategoryMutation";
import useGreetCategoriesStatsQuery from "../hooks/useGreetCategoriesStatsQuery";
import useLogosQuery from "../../Logos/hooks/useLogosQuery";
import DSTypography from "../../../system-design/DSTypography";
import DSDialog from "../../../system-design/DSDialog";
import DSDialogTitle from "../../../system-design/DSDialogTitle";
import DSDialogContent from "../../../system-design/DSDialogContent";
import DSDialogActions from "../../../system-design/DSDialogActions";
import DSSecondaryButton from "../../../system-design/DSSecondaryButton";
import DSPrimaryButton from "../../../system-design/DSPrimaryButton";
import DSErrorButton from "../../../system-design/DSErrorButton";
import DSAlert from "../../../system-design/DSAlert";
import DSTextField from "../../../system-design/DSTextField";
import DSStack from "../../../system-design/DSStack";
import DSUploadButton from "../../../system-design/DSUploadButton";

export type CategoryRow = {
  index: JSX.Element;
  name: string;
  numKlydos: number;
  numberOfGreets: number;
  image: JSX.Element;
  pendulumImage: JSX.Element;
  delete: JSX.Element;
};
function GiftCategories() {
  const { data: categories } = useCategoriesQuery();
  const { data: greetStats } = useGreetCategoriesStatsQuery();
  const { mutate: addCategoryMutation } = useAddCategoryMutation();
  const { mutate: removeCategoryMutation } = useRemoveCategoryMutation();
  const { mutate: updateCategoryMutation } = useUpdateCategoryMutation();
  const [showAddGiftCategory, setShowAddGiftCategory] =
    useState<boolean>(false);
  const [giftToDelete, setGiftToDelete] = useState("");
  const { data: logos, isLoading: logosLoading } = useLogosQuery();

  const GiftCategoryModalWithLogos = (modalProps: any) => (
    <GiftCategoryModal
      {...modalProps}
      logos={logos}
      logosLoading={logosLoading}
    />
  );

  const tableColumns: Array<colType> = [
    { label: "index", field: "index", sort: false },
    { label: "category name", field: "name", sort: true },
    { label: "collection image", field: "image", sort: false },
    { label: "pendulum logo", field: "pendulumImage", sort: false },
    { label: "num klydos", field: "numKlydos", sort: true },
    { label: "number of greets", field: "numberOfGreets", sort: false },
    {
      size: 32,
      noHeadline: true,
      sort: false,
      label: "",
      field: "delete",
    },
  ];
  const onIndexChange = (id: string, newIndex: number) => {
    if (isNaN(newIndex)) return;
    const currentIndex = categories!.find((c) => c._id === id)!.index;
    const categoriesAndIndex = categories!.map((category) => ({
      _id: category._id,
      index: category.index,
    }));
    const [movedItem] = categoriesAndIndex.splice(currentIndex, 1);

    categoriesAndIndex.splice(newIndex, 0, movedItem);

    categoriesAndIndex.forEach((item, index) => {
      item.index = index;
    });

    const changedCategories = categoriesAndIndex.filter(
      (category) =>
        categories?.find((c) => c._id === category._id)?.index !==
        category.index,
    );

    changedCategories.forEach((category) => {
      updateCategoryMutation({
        _id: category._id,
        index: category.index,
      });
    });
  };

  const tableRows = (category: GiftCategory): CategoryRow => {
    const greetCount =
      greetStats?.find((stat) => stat.category === category.name)?.greetCount ||
      0;

    return {
      index: (
        <DSTextField
          size="small"
          defaultValue={category.index}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onIndexChange(category._id, +e.target.value)}
          sx={{ width: "72px" }}
        />
      ),
      name: category.name,
      image: (
        <img width={100} src={category.categoryImageUrl} alt={category.name} />
      ),
      pendulumImage: category.pendulumLogoUrl ? (
        <img width={100} src={category.pendulumLogoUrl} alt={category.name} />
      ) : (
        <DSTypography sx={{ textAlign: "center" }}>
          No logo pendulum selected
        </DSTypography>
      ),

      numKlydos: category.klydoIds?.length || 0,
      numberOfGreets: greetCount,
      delete: (
        <FaTrash
          style={{ margin: "8px", cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            setGiftToDelete(category._id);
          }}
        />
      ),
    };
  };

  const DeleteGiftModal = () => {
    return (
      <>
        {giftToDelete && (
          <DSDialog onClose={() => setGiftToDelete("")} open fullWidth>
            <DSDialogTitle>Confirm Deletion</DSDialogTitle>
            <DSDialogContent>
              <DSTypography>
                Are you sure you want to delete the Greet Category?
              </DSTypography>
            </DSDialogContent>
            <DSDialogActions>
              <DSSecondaryButton onClick={() => setGiftToDelete("")}>
                Cancel
              </DSSecondaryButton>
              <DSErrorButton
                onClick={() => {
                  removeCategoryMutation(giftToDelete);
                  setGiftToDelete("");
                }}
              >
                Delete
              </DSErrorButton>
            </DSDialogActions>
          </DSDialog>
        )}
      </>
    );
  };

  const AddNewCategoryModal = () => {
    const [newCategoryName, setNewCategoryName] = useState("");
    const [newCategoryIcon, setNewCategoryIcon] = useState("");
    const [showWarning, setShowWarning] = useState(false);
    const addNewCategory = async () => {
      if (!newCategoryName || !newCategoryIcon) {
        setShowWarning(true);
        return;
      }
      setShowWarning(false);
      addCategoryMutation({
        categoryImageUrl: newCategoryIcon,
        name: newCategoryName,
      });
    };
    return (
      <>
        <DSDialogTitle>Add New Greet Category</DSDialogTitle>
        <DSDialogContent>
          {showWarning && (
            <DSAlert severity="error" sx={{ mb: 2 }}>
              Both Category Name and Category Icon are required!
            </DSAlert>
          )}
          <form>
            <DSStack spacing={2} sx={{ mt: 1 }}>
              <DSTextField
                label="Gift Category Name"
                placeholder="Enter Greet category name"
                fullWidth
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
              />
              <label>
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => {
                    const file = (e.target as HTMLInputElement)?.files?.[0];
                    if (file) {
                      const fileReader = new FileReader();
                      fileReader.onload = () => {
                        uploadFile({
                          file: fileReader.result as string,
                          folder: "giftCategories",
                          upload_preset: "packpendulumconvers",
                        }).then((url) => setNewCategoryIcon(url));
                      };
                      fileReader.readAsDataURL(file);
                    }
                  }}
                />
                <DSUploadButton text="Category Icon" />
              </label>
              {newCategoryIcon && (
                <DSTypography variant="caption" color="success.main">
                  Icon uploaded
                </DSTypography>
              )}
            </DSStack>
          </form>
        </DSDialogContent>
        <DSDialogActions>
          <DSSecondaryButton onClick={() => setShowAddGiftCategory(false)}>
            Cancel
          </DSSecondaryButton>
          <DSPrimaryButton
            onClick={() => {
              addNewCategory();
              setShowAddGiftCategory(false);
            }}
          >
            Add Gift Category
          </DSPrimaryButton>
        </DSDialogActions>
      </>
    );
  };

  return (
    <>
      {categories && (
        <>
          <MsTable
            id="Greet Categories"
            data={categories}
            cols={tableColumns}
            rowBuild={tableRows}
            title="Greet Categories"
            search={(row: CategoryRow, val: string) => {
              return row.name.includes(val);
            }}
            cancelSort
            modal={GiftCategoryModalWithLogos}
          />
          <FloatAddButton onClick={() => setShowAddGiftCategory(true)} />
        </>
      )}
      <DeleteGiftModal />
      {showAddGiftCategory && (
        <DSDialog
          open={true}
          fullWidth
          maxWidth="md"
          onClose={() => setShowAddGiftCategory(false)}
        >
          <AddNewCategoryModal />
        </DSDialog>
      )}
    </>
  );
}

export default GiftCategories;
